import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";
import MapPicker from "./screens/new/MapPicker";
import LocationInput from "./screens/new/LocationInput";
import Modal from "react-modal";
import { FaMapMarkerAlt, FaHistory, FaTimes } from "react-icons/fa";
import "./App.css";

const LOCAL_STORAGE_KEY_START = "recentStartLocations";
const LOCAL_STORAGE_KEY_DESTINATION = "recentDestinationLocations";

const Locate = () => {
  const [startLocation, setStartLocation] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [startCoordinates, setStartCoordinates] = useState(null);
  const [destinationCoordinates, setDestinationCoordinates] = useState(null);
  const [isStartMapOpen, setStartMapOpen] = useState(false);
  const [isDestinationMapOpen, setDestinationMapOpen] = useState(false);

  const [recentStartLocations, setRecentStartLocations] = useState([]);
  const [recentDestinationLocations, setRecentDestinationLocations] = useState(
    []
  );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries: ["places"],
  });

  const reverseGeocode = async (lat, lng, apiKey) => {
    try {
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
      const response = await fetch(geocodeUrl);
      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          return data.results[0].formatted_address;
        }
      }
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  useEffect(() => {
    const loadRecentLocations = () => {
      const startLocations =
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_START)) || [];
      const destinationLocations =
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_DESTINATION)) || [];
      setRecentStartLocations(startLocations);
      setRecentDestinationLocations(destinationLocations);
    };

    loadRecentLocations();
  }, []);

  const handleLocationSelect = async (coords, type, address) => {
    // If no address provided, use reverse geocoding
    let finalAddress = address;
    if (!finalAddress || finalAddress.trim() === "") {
      finalAddress = await reverseGeocode(coords.lat, coords.lng, getGoogleMapsApiKey());
    }

    if (type === "start") {
      setStartCoordinates(coords);
      setStartLocation(finalAddress);
      setStartMapOpen(false);
      updateRecentLocations(LOCAL_STORAGE_KEY_START, finalAddress, coords);
    } else {
      setDestinationCoordinates(coords);
      setDestinationLocation(finalAddress);
      setDestinationMapOpen(false);
      updateRecentLocations(LOCAL_STORAGE_KEY_DESTINATION, finalAddress, coords);
    }
  };

  const updateRecentLocations = (key, address, coords) => {
    const recent = JSON.parse(localStorage.getItem(key)) || [];
    const updated = recent.filter((loc) => loc.address !== address);
    const newEntry = { address, coords };
    const newRecentLocations = [newEntry, ...updated].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(newRecentLocations));

    if (key === LOCAL_STORAGE_KEY_START) {
      setRecentStartLocations(newRecentLocations);
    } else {
      setRecentDestinationLocations(newRecentLocations);
    }
  };

  const handleRecentLocationSelect = (coords, type, address) => {
    handleLocationSelect(coords, type, address);
    if (type === "start") {
      setStartLocation(address);
    } else {
      setDestinationLocation(address);
    }
  };

  const handleSubmit = async () => {
    if (!startCoordinates || !destinationCoordinates) {
      alert("Please select both start and destination locations.");
      return;
    }

    // Ensure both locations have proper addresses via reverse geocoding if needed
    let finalStartLocation = startLocation;
    let finalDestinationLocation = destinationLocation;

    if (!finalStartLocation || finalStartLocation.trim() === "") {
      finalStartLocation = await reverseGeocode(
        startCoordinates.lat,
        startCoordinates.lng,
        getGoogleMapsApiKey()
      );
      setStartLocation(finalStartLocation);
    }

    if (!finalDestinationLocation || finalDestinationLocation.trim() === "") {
      finalDestinationLocation = await reverseGeocode(
        destinationCoordinates.lat,
        destinationCoordinates.lng,
        getGoogleMapsApiKey()
      );
      setDestinationLocation(finalDestinationLocation);
    }

    const message = {
      pointA: {
        ...startCoordinates,
        address: finalStartLocation,
      },
      pointB: {
        ...destinationCoordinates,
        address: finalDestinationLocation,
      },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  };

  const customModalStyles = {
    content: {
      top: "0%",
      left: "0%",
      right: "0%",
      bottom: "0%",
      transform: "none",
      width: "100%",
      height: "100%",
      padding: "0",
      border: "none",
      borderRadius: "0",
      overflow: "hidden",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.75)",
    },
  };
  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          Location Picker
        </h1>

        {/* Start Location Container */}
        <div className="mb-6 p-4 border rounded-lg shadow-sm bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Location
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <LocationInput
                placeholder="Enter start location"
                value={startLocation}
                onChange={setStartLocation}
                onSelect={(coords, address) =>
                  handleLocationSelect(coords, "start", address)
                }
              />
            </div>
            <button
              onClick={() => setStartMapOpen(true)}
              className="p-2 bg-[#ffc000] hover:bg-[#e3ab00] text-white rounded-lg transition-colors"
            >
              <FaMapMarkerAlt className="w-5 h-5" />
            </button>
          </div>

          {/* Recent Start Locations */}
          {recentStartLocations.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <FaHistory className="w-4 h-4" />
                <span>Recent Locations</span>
              </div>
              <div className="space-y-2">
                {recentStartLocations.map((location, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      handleRecentLocationSelect(
                        location.coords,
                        "start",
                        location.address
                      )
                    }
                    className="p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors text-sm"
                  >
                    {location.address}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Destination Location Container */}
        <div className="mb-8 p-4 border rounded-lg shadow-sm bg-gray-50">
          {" "}
          {/* Added a container */}
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Destination Location
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <LocationInput
                placeholder="Enter destination location"
                value={destinationLocation}
                onChange={setDestinationLocation}
                onSelect={(coords, address) =>
                  handleLocationSelect(coords, "destination", address)
                }
              />
            </div>
            <button
              onClick={() => setDestinationMapOpen(true)}
              className="p-2 bg-[#ffc000] hover:bg-[#e3ab00] text-white rounded-lg transition-colors"
            >
              <FaMapMarkerAlt className="w-5 h-5" />
            </button>
          </div>
          {/* Recent Destination Locations */}
          {recentDestinationLocations.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <FaHistory className="w-4 h-4" />
                <span>Recent Locations</span>
              </div>
              <div className="space-y-2">
                {recentDestinationLocations.map((location, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      handleRecentLocationSelect(
                        location.coords,
                        "destination",
                        location.address
                      )
                    }
                    className="p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors text-sm"
                  >
                    {location.address}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleSubmit}
          className="w-full bg-[#ffc000] text-black hover:bg-[#e3ab00] text-gray-900 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!startCoordinates || !destinationCoordinates}
        >
          Continue
        </button>
      </div>

      {/* Modals */}
      <Modal
        isOpen={isStartMapOpen}
        onRequestClose={() => setStartMapOpen(false)}
        style={customModalStyles}
      >
        <div className="relative pt-9">
          <div className="p-4 border-b">
            <h2 className="text-xl font-semibold">Select Start Location</h2>
          </div>
          <button
            onClick={() => setStartMapOpen(false)}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 pt-9"
          >
            <FaTimes className="w-5 h-5" />
          </button>
          <MapPicker
            onLocationSelect={(coords, address) =>
              handleLocationSelect(coords, "start", address)
            }
          />
        </div>
      </Modal>

      <Modal
        isOpen={isDestinationMapOpen}
        onRequestClose={() => setDestinationMapOpen(false)}
        style={customModalStyles}
      >
        <div className="relative pt-9">
          <div className="p-4 border-b">
            <h2 className="text-xl font-semibold ">
              Select Destination Location
            </h2>
          </div>
          <button
            onClick={() => setDestinationMapOpen(false)}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 pt-8"
          >
            <FaTimes className="w-5 h-5" />
          </button>
          <MapPicker
            onLocationSelect={(coords, address) =>
              handleLocationSelect(coords, "destination", address)
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default Locate;
